import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X, ChevronRight, Shield, Sparkles, Search, Zap, BarChart3, TrendingUp, MessageCircle, Edit3, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const LandingPage = () => {
  return (
    <div className="landing-page font-['Montserrat',sans-serif] bg-white text-gray-900">
      {/* NAV */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-[1040px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#1a7a3c] rounded-lg flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg">SIFT</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <a href="#products" className="hover:text-gray-900 transition-colors">Products</a>
            <a href="#howitworks" className="hover:text-gray-900 transition-colors">How It Works</a>
            <a href="#intelligence" className="hover:text-gray-900 transition-colors">Intelligence</a>
            <a href="#compare" className="hover:text-gray-900 transition-colors">Compare</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/app">
              <Button variant="outline" size="sm" className="text-sm">Launch App</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="py-20 px-6">
        <div className="max-w-[1040px] mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#f0faf4] rounded-full border border-[#d4ead9] mb-8">
            <span className="w-2 h-2 bg-[#1a7a3c] rounded-full animate-pulse" />
            <span className="text-sm font-medium text-[#1a7a3c]">Search Intelligence for Feed Transformation</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-tight">
            Two tools. One <em className="not-italic text-[#1a7a3c]">feed intelligence</em> platform.
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed">
            Fix your GMC feed health in minutes with SIFT Lite. Or go deeper with the full signal intelligence engine. Both built for Shopify DTC brands.
          </p>
        </div>
      </section>

      {/* ══ TWO PRODUCTS ═══════════════════════════════════════════════════════ */}
      <section id="products" className="pb-20 px-6">
        <div className="max-w-[1040px] mx-auto grid md:grid-cols-2 gap-6">

          {/* ── SIFT LITE ── */}
          <div className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden hover:border-[#4285f4] transition-colors group">
            <div className="bg-gradient-to-r from-[#eff6ff] to-[#f0f9ff] px-8 py-6 border-b border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#4285f4] rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-extrabold">SIFT Lite</h2>
                  <p className="text-xs font-semibold text-[#4285f4] uppercase tracking-wider">GMC Feed Health</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Fix the GMC errors silently killing your Shopping impressions. Colour, gender, GTIN, title quality — diagnosed and resolved in one pass.
              </p>
            </div>
            <div className="px-8 py-6">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-4">3-step workflow</div>
              <div className="space-y-3 mb-6">
                {[
                  { num: '01', title: 'Connect Shopify', desc: 'Pull your product catalogue and detect existing GMC issues automatically.' },
                  { num: '02', title: 'Scan & Diagnose', desc: 'Missing colour, gender, age group, GTIN, title quality — all flagged with severity and fix suggestions.' },
                  { num: '03', title: 'Fix & Push', desc: 'Apply AI-proposed fixes and push corrected attributes back to Shopify in one click.' },
                ].map(s => (
                  <div key={s.num} className="flex gap-3">
                    <div className="w-7 h-7 rounded-lg bg-[#eff6ff] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[10px] font-extrabold text-[#4285f4]">{s.num}</span>
                    </div>
                    <div>
                      <div className="text-sm font-bold">{s.title}</div>
                      <div className="text-xs text-gray-500 leading-relaxed">{s.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mb-6">
                {['Colour', 'Gender', 'Age group', 'GTIN', 'Title quality', 'Material', 'Description'].map(f => (
                  <span key={f} className="text-[10px] font-semibold px-2 py-1 bg-[#eff6ff] text-[#4285f4] rounded border border-[#bfdbfe]">{f}</span>
                ))}
              </div>
              <Link to="/app?demo=lite" data-testid="demo-lite-btn">
                <Button className="w-full bg-[#4285f4] hover:bg-[#3574d4]">
                  Try Lite demo <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* ── SIFT APP ── */}
          <div className="bg-white rounded-2xl border-2 border-[#d4ead9] overflow-hidden hover:border-[#1a7a3c] transition-colors group relative">
            <div className="absolute top-4 right-4">
              <span className="text-[10px] font-bold px-2 py-1 bg-[#1a7a3c] text-white rounded">FULL SUITE</span>
            </div>
            <div className="bg-gradient-to-r from-[#f0fdf4] to-[#ecfdf5] px-8 py-6 border-b border-[#d4ead9]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#1a7a3c] rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-extrabold">SIFT App</h2>
                  <p className="text-xs font-semibold text-[#1a7a3c] uppercase tracking-wider">Signal Intelligence</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Full product enrichment using live consumer search signals from 11 sources. AI-optimised copy, structured titles, multi-channel output.
              </p>
            </div>
            <div className="px-8 py-6">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-4">7-step workflow</div>
              <div className="space-y-3 mb-6">
                {[
                  { num: '01', title: 'Brand Setup', desc: 'Configure tone of voice, guardrail words, and brand voice notes.' },
                  { num: '02', title: 'Search Intelligence', desc: '11 live signal sources per SKU — GSC, GA4, Reddit, TikTok, Amazon, Trends...' },
                  { num: '03', title: 'Feed Audit + GMC Fix', desc: 'Full feed health score, issue diagnostics, and attribute fixes.' },
                  { num: '04', title: 'Review & Push', desc: 'AI copy generation, channel title engine, before/after preview, approve and push.' },
                ].map(s => (
                  <div key={s.num} className="flex gap-3">
                    <div className="w-7 h-7 rounded-lg bg-[#f0fdf4] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[10px] font-extrabold text-[#1a7a3c]">{s.num}</span>
                    </div>
                    <div>
                      <div className="text-sm font-bold">{s.title}</div>
                      <div className="text-xs text-gray-500 leading-relaxed">{s.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">Includes everything in Lite, plus</div>
              <div className="flex flex-wrap gap-2 mb-6">
                {['AI Copy Engine', 'Title Engine', 'Reddit Signals', 'Channel Formats', 'Revenue Model', 'SEO/GEO/AEO'].map(f => (
                  <span key={f} className="text-[10px] font-semibold px-2 py-1 bg-[#f0fdf4] text-[#1a7a3c] rounded border border-[#d4ead9]">{f}</span>
                ))}
              </div>
              <Link to="/app?demo=true" data-testid="demo-full-btn">
                <Button className="w-full bg-[#1a7a3c] hover:bg-[#145f2e]">
                  Try Full demo <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="py-16 px-6 bg-gray-50 border-y border-gray-200">
        <div className="max-w-[1040px] mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-[#1a7a3c] mb-2 block">Before & After</span>
            <h2 className="text-3xl font-bold mb-3">The difference feed intelligence makes</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <span className="inline-block px-3 py-1 bg-gray-200 rounded text-xs font-bold text-gray-600 mb-4">Before SIFT</span>
              <div className="flex items-center gap-2 text-red-600 font-bold text-sm mb-3">
                <span className="w-2 h-2 bg-red-600 rounded-full" /> Feed score: 28 / 100
              </div>
              <h3 className="font-bold text-lg mb-2 uppercase">Black Cargo Trousers</h3>
              <p className="text-gray-400 italic text-sm mb-4">No description. No attributes.</p>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2"><X className="w-4 h-4 text-red-500" /> Missing colour, gender, GTIN</div>
                <div className="flex items-center gap-2"><X className="w-4 h-4 text-red-500" /> Title too short for Shopping</div>
                <div className="flex items-center gap-2"><X className="w-4 h-4 text-red-500" /> Zero search signals connected</div>
                <div className="flex items-center gap-2"><X className="w-4 h-4 text-red-500" /> Invisible on long-tail queries</div>
              </div>
            </div>
            <div className="bg-[#f0fdf4] rounded-xl p-6 border border-[#d4ead9]">
              <span className="inline-block px-3 py-1 bg-[#1a7a3c] text-white rounded text-xs font-bold mb-4">After SIFT</span>
              <div className="flex items-center gap-2 text-[#1a7a3c] font-bold text-sm mb-3">
                <Check className="w-4 h-4" /> Feed score: 88 / 100
              </div>
              <h3 className="font-bold text-lg mb-2">SIFT CLOTHING Black Cargo Trousers - Wide Leg Technical Ripstop | Relaxed Fit | Utility Detailing</h3>
              <p className="text-gray-700 text-sm mb-4 leading-relaxed">Wide-leg black cargo trousers in technical ripstop fabric. Six-pocket construction with utility detailing — oversized thigh pockets with press stud closure.</p>
              <div className="flex flex-wrap gap-2">
                {['wide leg', 'technical ripstop', 'six-pocket', 'relaxed fit', 'utility detailing'].map((chip, i) => (
                  <span key={i} className="px-2 py-1 bg-white rounded text-xs font-medium text-[#1a7a3c] border border-[#d4ead9]">{chip}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="howitworks" className="py-16 px-6">
        <div className="max-w-[1040px] mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-[#1a7a3c] mb-2 block">How It Works</span>
            <h2 className="text-3xl font-bold mb-3">Two workflows. One platform.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Lite flow */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 text-[#4285f4]" />
                <h3 className="font-bold text-lg">SIFT Lite</h3>
                <span className="text-[10px] font-bold px-2 py-0.5 bg-[#eff6ff] text-[#4285f4] rounded border border-[#bfdbfe]">GMC HEALTH</span>
              </div>
              <div className="space-y-3">
                {[
                  { icon: '1', title: 'Connect Shopify store', time: '30 seconds' },
                  { icon: '2', title: 'Auto-scan all GMC attributes', time: '~ 1 minute' },
                  { icon: '3', title: 'Apply fixes + push to Shopify', time: '1 click' },
                ].map(s => (
                  <div key={s.icon} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-200">
                    <div className="w-8 h-8 rounded-lg bg-[#eff6ff] flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-extrabold text-[#4285f4]">{s.icon}</span>
                    </div>
                    <div className="flex-1"><span className="text-sm font-bold">{s.title}</span></div>
                    <span className="text-[10px] font-semibold text-gray-400">{s.time}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Full flow */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-[#1a7a3c]" />
                <h3 className="font-bold text-lg">SIFT App</h3>
                <span className="text-[10px] font-bold px-2 py-0.5 bg-[#f0fdf4] text-[#1a7a3c] rounded border border-[#d4ead9]">FULL SUITE</span>
              </div>
              <div className="space-y-3">
                {[
                  { icon: '1', title: 'Brand voice + tone setup', time: '~ 2 mins' },
                  { icon: '2', title: 'Pull 11 live signal sources per SKU', time: 'Automatic' },
                  { icon: '3', title: 'Feed audit + GMC diagnostics', time: '~ 1 minute' },
                  { icon: '4', title: 'AI copy + title engine + GMC fix', time: '~ 3 mins' },
                  { icon: '5', title: 'Review, approve + push live', time: '1 click' },
                ].map(s => (
                  <div key={s.icon} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-200">
                    <div className="w-8 h-8 rounded-lg bg-[#f0fdf4] flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-extrabold text-[#1a7a3c]">{s.icon}</span>
                    </div>
                    <div className="flex-1"><span className="text-sm font-bold">{s.title}</span></div>
                    <span className="text-[10px] font-semibold text-gray-400">{s.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEARCH INTELLIGENCE - SIFT App only */}
      <section id="intelligence" className="py-16 px-6 bg-gray-50 border-y border-gray-200">
        <div className="max-w-[1040px] mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-[#1a7a3c] mb-2 block">SIFT App — Signal Intelligence</span>
            <h2 className="text-3xl font-bold mb-3">11 signal sources. One SKU-specific prompt.</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Every other AI copy tool writes from product attributes alone. SIFT writes from what real shoppers are searching, discussing and querying right now.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {[
              { icon: Search, color: '#4285f4', bg: '#eff6ff', title: 'Google Search Console', desc: 'Organic queries with impressions, clicks and position per product.' },
              { icon: BarChart3, color: '#34a853', bg: '#f0fdf4', title: 'GA4 On-site Search', desc: 'What visitors searched on your store that led to this product.' },
              { icon: MessageCircle, color: '#ff4500', bg: '#fff5f0', title: 'Reddit & Forums', desc: 'Verbatim consumer language from category-specific subreddits.' },
              { icon: TrendingUp, color: '#ea4335', bg: '#fef2f2', title: 'Google Trends', desc: 'Rising vs stable terms with seasonal peak windows.' },
            ].map((source, i) => (
              <div key={i} className="bg-white rounded-lg p-4 border border-gray-200" style={{ borderTopColor: source.color, borderTopWidth: '3px' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{ background: source.bg }}>
                  <source.icon className="w-4 h-4" style={{ color: source.color }} />
                </div>
                <h3 className="font-bold text-sm mb-1">{source.title}</h3>
                <p className="text-[11px] text-gray-600 leading-relaxed">{source.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-500">+ SEM, YouTube, Amazon, TikTok, Pinterest, Depop, Manual Keywords</p>
        </div>
      </section>

      {/* COMPARISON */}
      <section id="compare" className="py-16 px-6">
        <div className="max-w-[1040px] mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-[#1a7a3c] mb-2 block">Compare</span>
            <h2 className="text-3xl font-bold mb-3">Lite vs Full — what's included</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-bold">Feature</th>
                  <th className="py-3 px-4 font-bold bg-[#eff6ff] text-[#4285f4]">SIFT Lite</th>
                  <th className="py-3 px-4 font-bold bg-[#f0fdf4] text-[#1a7a3c]">SIFT App</th>
                </tr>
              </thead>
              <tbody className="text-xs">
                {[
                  { f: 'GMC error auto-diagnosis', lite: true, full: true },
                  { f: 'AI-proposed attribute fixes', lite: true, full: true },
                  { f: 'One-click push to Shopify', lite: true, full: true },
                  { f: 'Feed health scoring', lite: true, full: true },
                  { f: 'Title quality analysis', lite: true, full: true },
                  { f: 'Live GSC query data per SKU', lite: false, full: true },
                  { f: '11 signal sources per product', lite: false, full: true },
                  { f: 'AI copy generation (Claude)', lite: false, full: true },
                  { f: 'Deterministic title engine', lite: false, full: true },
                  { f: 'Channel format variants', lite: false, full: true },
                  { f: 'Reddit consumer language', lite: false, full: true },
                  { f: 'Revenue impact modelling', lite: false, full: true },
                  { f: 'SEO / GEO / AEO optimisation', lite: false, full: true },
                  { f: 'Brand voice configuration', lite: false, full: true },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-3 px-4">{row.f}</td>
                    <td className="py-3 px-4 bg-[#eff6ff]/50 text-center">{row.lite ? <Check className="w-4 h-4 text-[#4285f4] mx-auto" /> : <X className="w-4 h-4 text-gray-300 mx-auto" />}</td>
                    <td className="py-3 px-4 bg-[#f0fdf4]/50 text-center"><Check className="w-4 h-4 text-[#1a7a3c] mx-auto" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-[600px] mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Try it now</h2>
          <p className="text-gray-600 mb-8">No account needed. Explore the interactive demo for both products.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/app?demo=lite">
              <Button variant="outline" size="lg" className="px-8 border-[#4285f4] text-[#4285f4] hover:bg-[#eff6ff]">
                <Shield className="w-4 h-4 mr-2" /> SIFT Lite demo
              </Button>
            </Link>
            <Link to="/app?demo=true">
              <Button size="lg" className="bg-[#1a7a3c] hover:bg-[#145f2e] px-8">
                <Sparkles className="w-4 h-4 mr-2" /> SIFT App demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 px-6 border-t border-gray-200">
        <div className="max-w-[1040px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#1a7a3c] rounded flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
            <span className="font-bold text-sm">SIFT</span>
          </div>
          <p className="text-xs text-gray-500">© 2026 SIFT — Search Intelligence for Feed Transformation</p>
          <Link to="/app">
            <Button variant="outline" size="sm" className="text-xs">Launch App</Button>
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
